import styled, { css } from 'styled-components';

import Image from 'components/Image';
import Roundel from 'components/Roundel';
import Slider from 'components/Slider';

import { BackgroundTexture } from 'styles/global';
import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import { Grid, columns } from 'styles/grid';
import { H3, H5, P1 } from 'styles/typography';

import { isEven } from 'utils/helpers';

const Section = styled.section`
  padding: 84px 0;
  background-color: ${COLORS.pinkMillennial};
  color: ${COLORS.forest};
  ${BackgroundTexture}

  ${BREAKPOINTS.small`
    padding: 164px 0 150px;
  `}
`;

const Heading = styled(H3)`
  ${BREAKPOINTS.small`
    margin-bottom: 115px;
  `}
`;

const CardRoundel = styled(Roundel)`
  position: absolute;
  right: 10px;
  bottom: -20px;
  transform: rotate(-10deg);
  transition: transform 0.4s ease-in-out 0.5s;

  ${BREAKPOINTS.small`
    right: -40px;
    left: ${(props) => (props.reverse ? '-40px' : 'auto')};
    top: 20px;
    transform: ${(props) =>
      props.reverse ? 'rotate(10deg)' : 'rotate(-10deg)'};
  `}
`;

const MobileLayout = styled.div`
  .slick-active ${CardRoundel} {
    transform: rotate(10deg);
  }

  ${BREAKPOINTS.small`
    display: none;
  `}
`;

const DesktopLayout = styled.div`
  display: none;

  ${BREAKPOINTS.small`
    display: block;
  `}
`;

const Hr = styled.hr`
  ${columns(2, 10)}
  display: none;
  margin: 50px 0;
  width: 100%;
  border: 0;
  height: 1px;
  background-color: ${COLORS.dawn.opacity(0.2)};

  ${BREAKPOINTS.small`
    display: block;
  `}
`;

const Card = styled(Grid)`
  padding: 60px var(--gutter-side) 28px;

  &:last-child ${Hr} {
    display: none;
  }

  ${BREAKPOINTS.small`
    padding: 0;
    display: flex;
  `}
`;

const CardInner = styled.div`
  ${columns(null, 12)}
  margin-bottom: 20px;
  position: relative;

  ${BREAKPOINTS.small`
    margin-bottom: 0;
    ${(props) => (props.reverse ? columns(7, 5) : columns(2, 5))}
  `}

  ${BREAKPOINTS.medium`
    margin-right: 40px;
    margin: ${(props) => (props.reverse ? '0 0 0 40px' : '0 40px 0 0')}
  `}
`;

const CardImage = styled(Image)`
  max-height: 220px;

  ${BREAKPOINTS.small`
    max-height: 300px;
  `}
`;

const CardContent = styled.div`
  ${columns(null, 12)}

  ${BREAKPOINTS.small`
    ${columns(7, 5)}
    padding-left: 40px;
    max-width: 420px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    justify-self: start;

    ${(props) =>
      props.reverse &&
      css`
        ${columns(2, 5)}
        padding: 0 40px 0 0;
        justify-self: end;
      `}
  `}
`;

const Item = ({ item: { title, image, description }, index }) => {
  const itemNo = index >= 10 ? index : `0${index}`;
  return (
    <Card>
      <CardInner reverse={isEven(index)}>
        <CardImage image={image} />
        <CardRoundel reverse={isEven(index)}>{itemNo}</CardRoundel>
      </CardInner>
      <CardContent reverse={isEven(index)}>
        <H5 as="h3" small weight="semibold">
          {title}
        </H5>
        <P1 indent>{description}</P1>
      </CardContent>
      <Hr />
    </Card>
  );
};

const AboutSection = ({ title, sections }) => {
  return (
    <Section>
      <Heading as="h2" small align="center" weight="semibold">
        {title}
      </Heading>
      <MobileLayout>
        <Slider>
          {sections?.map((item, i) => (
            <Item key={item.title} item={item} index={i + 1} />
          ))}
        </Slider>
      </MobileLayout>

      <DesktopLayout>
        {sections?.map((item, i) => (
          <Item key={item.title} item={item} index={i + 1} />
        ))}
      </DesktopLayout>
    </Section>
  );
};

export default AboutSection;
